import React, { useState } from 'react';
import { XIcon, ExternalLinkIcon, DownloadIcon, BookmarkIcon, MessageSquareIcon, TagIcon, PaperclipIcon, LinkIcon, FileTextIcon, ShoppingCartIcon, ReceiptIcon, MinusSquareIcon, CreditCardIcon, FileSignatureIcon, CheckCircleIcon, AlertTriangleIcon, ClockIcon, XCircleIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { StatusBadge } from '../StatusBadge';
export function DocumentPreview({
  document,
  onClose,
  onOpenDocument,
  locale = 'en-US'
}) {
  const [activeTab, setActiveTab] = useState('preview');
  // Format currency based on locale
  const formatCurrency = amount => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  // Format date based on locale
  const formatDate = dateString => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Get document type icon
  const getDocumentTypeIcon = typeCode => {
    switch (typeCode) {
      case 'INV':
        return <ReceiptIcon className="h-5 w-5 text-purple-500" data-id="element-4713" />;
      case 'PO':
        return <ShoppingCartIcon className="h-5 w-5 text-blue-500" data-id="element-4714" />;
      case 'REQ':
        return <FileTextIcon className="h-5 w-5 text-green-500" data-id="element-4715" />;
      case 'CN':
        return <MinusSquareIcon className="h-5 w-5 text-red-500" data-id="element-4716" />;
      case 'EXP':
        return <CreditCardIcon className="h-5 w-5 text-yellow-500" data-id="element-4717" />;
      case 'CTR':
        return <FileSignatureIcon className="h-5 w-5 text-indigo-500" data-id="element-4718" />;
      default:
        return <FileTextIcon className="h-5 w-5 text-gray-500" data-id="element-4719" />;
    }
  };
  // Get status badge type
  const getStatusBadgeType = status => {
    switch (status) {
      case 'Approved':
      case 'Completed':
      case 'Paid':
      case 'Active':
      case 'Processed':
        return 'success';
      case 'Pending Approval':
      case 'Sent to Vendor':
        return 'info';
      case 'Draft':
        return 'default';
      case 'Exception':
      case 'Rejected':
        return 'error';
      default:
        return 'default';
    }
  };
  return <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-4720">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" data-id="element-4721">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-4722">
          <div className="flex items-center" data-id="element-4723">
            {getDocumentTypeIcon(document.typeCode)}
            <h3 className="ml-2 text-lg font-medium text-gray-900" data-id="element-4724">
              {document.id}
            </h3>
            <StatusBadge status={getStatusBadgeType(document.status)} text={document.status} className="ml-3" data-id="element-4725" />
          </div>
          <div className="flex items-center space-x-2" data-id="element-4726">
            <button onClick={onOpenDocument} className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none" data-id="element-4727">
              <ExternalLinkIcon className="h-4 w-4 mr-1" data-id="element-4728" />
              Open
            </button>
            <button onClick={onClose} className="inline-flex items-center justify-center h-8 w-8 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-4729">
              <XIcon className="h-5 w-5" data-id="element-4730" />
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="flex border-b border-gray-200" data-id="element-4731">
          <button className={`px-4 py-2 text-sm font-medium ${activeTab === 'preview' ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('preview')} data-id="element-4732">
            Preview
          </button>
          <button className={`px-4 py-2 text-sm font-medium ${activeTab === 'details' ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('details')} data-id="element-4733">
            Details
          </button>
          <button className={`px-4 py-2 text-sm font-medium ${activeTab === 'related' ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('related')} data-id="element-4734">
            Related Documents
          </button>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-auto p-6" data-id="element-4735">
          {activeTab === 'preview' && <div className="flex items-center justify-center h-full" data-id="element-4736">
              {document.previewUrl ? <img src={document.previewUrl} alt={`Preview of ${document.id}`} className="max-w-full max-h-full object-contain" data-id="element-4737" /> : <div className="text-center p-10 bg-gray-50 rounded-lg" data-id="element-4738">
                  <FileTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" data-id="element-4739" />
                  <p className="text-gray-500" data-id="element-4740">
                    No preview available for this document.
                  </p>
                  <button onClick={onOpenDocument} className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none" data-id="element-4741">
                    <ExternalLinkIcon className="h-4 w-4 mr-2" data-id="element-4742" />
                    Open Document
                  </button>
                </div>}
            </div>}
          {activeTab === 'details' && <div className="space-y-6" data-id="element-4743">
              <div className="grid grid-cols-2 gap-6" data-id="element-4744">
                <div data-id="element-4745">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4746">
                    Document Information
                  </h4>
                  <dl className="grid grid-cols-3 gap-x-4 gap-y-2" data-id="element-4747">
                    <dt className="text-sm font-medium text-gray-500" data-id="element-4748">Type</dt>
                    <dd className="text-sm text-gray-900 col-span-2" data-id="element-4749">
                      {document.type}
                    </dd>
                    <dt className="text-sm font-medium text-gray-500" data-id="element-4750">ID</dt>
                    <dd className="text-sm text-gray-900 col-span-2" data-id="element-4751">
                      {document.id}
                    </dd>
                    <dt className="text-sm font-medium text-gray-500" data-id="element-4752">Date</dt>
                    <dd className="text-sm text-gray-900 col-span-2" data-id="element-4753">
                      {formatDate(document.date)}
                    </dd>
                    {document.dueDate && <>
                        <dt className="text-sm font-medium text-gray-500" data-id="element-4754">
                          Due Date
                        </dt>
                        <dd className="text-sm text-gray-900 col-span-2" data-id="element-4755">
                          {formatDate(document.dueDate)}
                        </dd>
                      </>}
                    <dt className="text-sm font-medium text-gray-500" data-id="element-4756">
                      Amount
                    </dt>
                    <dd className="text-sm text-gray-900 col-span-2" data-id="element-4757">
                      {formatCurrency(document.amount)}
                    </dd>
                    <dt className="text-sm font-medium text-gray-500" data-id="element-4758">
                      Status
                    </dt>
                    <dd className="text-sm text-gray-900 col-span-2" data-id="element-4759">
                      <StatusBadge status={getStatusBadgeType(document.status)} text={document.status} data-id="element-4760" />
                    </dd>
                  </dl>
                </div>
                <div data-id="element-4761">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4762">
                    Organization Information
                  </h4>
                  <dl className="grid grid-cols-3 gap-x-4 gap-y-2" data-id="element-4763">
                    <dt className="text-sm font-medium text-gray-500" data-id="element-4764">
                      Vendor
                    </dt>
                    <dd className="text-sm text-gray-900 col-span-2" data-id="element-4765">
                      {document.vendor || 'N/A'}
                    </dd>
                    <dt className="text-sm font-medium text-gray-500" data-id="element-4766">
                      Entity
                    </dt>
                    <dd className="text-sm text-gray-900 col-span-2" data-id="element-4767">
                      {document.entity}
                    </dd>
                    <dt className="text-sm font-medium text-gray-500" data-id="element-4768">Site</dt>
                    <dd className="text-sm text-gray-900 col-span-2" data-id="element-4769">
                      {document.site}
                    </dd>
                  </dl>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-6" data-id="element-4770">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4771">
                  Metadata
                </h4>
                <dl className="grid grid-cols-6 gap-x-4 gap-y-2" data-id="element-4772">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-4773">
                    Created By
                  </dt>
                  <dd className="text-sm text-gray-900 col-span-2" data-id="element-4774">
                    {document.createdBy}
                  </dd>
                  <dt className="text-sm font-medium text-gray-500" data-id="element-4775">
                    Created Date
                  </dt>
                  <dd className="text-sm text-gray-900 col-span-2" data-id="element-4776">
                    {formatDate(document.createdDate)}
                  </dd>
                  <dt className="text-sm font-medium text-gray-500" data-id="element-4777">
                    Last Modified
                  </dt>
                  <dd className="text-sm text-gray-900 col-span-2" data-id="element-4778">
                    {formatDate(document.lastModified)}
                  </dd>
                  {document.isImported && <>
                      <dt className="text-sm font-medium text-gray-500" data-id="element-4779">
                        OCR Confidence
                      </dt>
                      <dd className="text-sm text-gray-900 col-span-2" data-id="element-4780">
                        <div className="flex items-center" data-id="element-4781">
                          <div className="w-24 bg-gray-200 rounded-full h-2.5 mr-2" data-id="element-4782">
                            <div className={`h-2.5 rounded-full ${document.ocrConfidence >= 0.8 ? 'bg-green-500' : document.ocrConfidence >= 0.6 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                        width: `${document.ocrConfidence * 100}%`
                      }} data-id="element-4783"></div>
                          </div>
                          <span data-id="element-4784">
                            {Math.round(document.ocrConfidence * 100)}%
                          </span>
                        </div>
                      </dd>
                    </>}
                  {document.tags && document.tags.length > 0 && <>
                      <dt className="text-sm font-medium text-gray-500" data-id="element-4785">
                        Tags
                      </dt>
                      <dd className="text-sm text-gray-900 col-span-5" data-id="element-4786">
                        <div className="flex flex-wrap gap-2" data-id="element-4787">
                          {document.tags.map(tag => <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-4788">
                              {tag}
                            </span>)}
                        </div>
                      </dd>
                    </>}
                </dl>
              </div>
              {(document.hasAttachments || document.hasNotes || document.hasWarnings || document.hasErrors) && <div className="border-t border-gray-200 pt-6" data-id="element-4789">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4790">
                    Additional Information
                  </h4>
                  <dl className="grid grid-cols-6 gap-x-4 gap-y-2" data-id="element-4791">
                    {document.hasAttachments && <>
                        <dt className="text-sm font-medium text-gray-500" data-id="element-4792">
                          Attachments
                        </dt>
                        <dd className="text-sm text-gray-900 col-span-5" data-id="element-4793">
                          <div className="flex items-center" data-id="element-4794">
                            <PaperclipIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-4795" />
                            <span data-id="element-4796">
                              {document.attachmentCount} attachment(s)
                            </span>
                          </div>
                        </dd>
                      </>}
                    {document.hasNotes && <>
                        <dt className="text-sm font-medium text-gray-500" data-id="element-4797">
                          Notes
                        </dt>
                        <dd className="text-sm text-gray-900 col-span-5" data-id="element-4798">
                          <div className="flex items-center" data-id="element-4799">
                            <MessageSquareIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-4800" />
                            <span data-id="element-4801">{document.noteCount} note(s)</span>
                          </div>
                        </dd>
                      </>}
                    {document.hasWarnings && <>
                        <dt className="text-sm font-medium text-gray-500" data-id="element-4802">
                          Warnings
                        </dt>
                        <dd className="text-sm text-gray-900 col-span-5" data-id="element-4803">
                          <div className="flex items-center text-yellow-500" data-id="element-4804">
                            <AlertTriangleIcon className="h-4 w-4 mr-1" data-id="element-4805" />
                            <span data-id="element-4806">This document has warnings</span>
                          </div>
                        </dd>
                      </>}
                    {document.hasErrors && <>
                        <dt className="text-sm font-medium text-gray-500" data-id="element-4807">
                          Errors
                        </dt>
                        <dd className="text-sm text-gray-900 col-span-5" data-id="element-4808">
                          <div className="flex items-center text-red-500" data-id="element-4809">
                            <XCircleIcon className="h-4 w-4 mr-1" data-id="element-4810" />
                            <span data-id="element-4811">This document has errors</span>
                          </div>
                        </dd>
                      </>}
                  </dl>
                </div>}
            </div>}
          {activeTab === 'related' && <div data-id="element-4812">
              <h4 className="text-sm font-medium text-gray-500 mb-4" data-id="element-4813">
                Linked Documents
              </h4>
              {document.linkedDocuments && document.linkedDocuments.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-4814">
                  {document.linkedDocuments.map(docId => {
              // Find the linked document in our sample data
              // In a real app, this would come from the API
              const linkedDoc = [{
                id: 'INV-2023-001',
                type: 'Invoice',
                typeCode: 'INV',
                date: '2023-10-15',
                amount: 1250.75,
                status: 'Approved'
              }, {
                id: 'PO-2023-001',
                type: 'Purchase Order',
                typeCode: 'PO',
                date: '2023-10-05',
                amount: 1500.0,
                status: 'Sent to Vendor'
              }, {
                id: 'REQ-2023-002',
                type: 'Requisition',
                typeCode: 'REQ',
                date: '2023-09-28',
                amount: 1500.0,
                status: 'Approved'
              }, {
                id: 'CN-2023-001',
                type: 'Credit Note',
                typeCode: 'CN',
                date: '2023-10-20',
                amount: 250.0,
                status: 'Processed'
              }, {
                id: 'INV-2023-003',
                type: 'Invoice',
                typeCode: 'INV',
                date: '2023-10-18',
                amount: 4300.0,
                status: 'Exception'
              }, {
                id: 'PO-2023-010',
                type: 'Purchase Order',
                typeCode: 'PO',
                date: '2023-09-05',
                amount: 2000.0,
                status: 'Completed'
              }, {
                id: 'INV-2023-015',
                type: 'Invoice',
                typeCode: 'INV',
                date: '2023-09-10',
                amount: 2000.0,
                status: 'Paid'
              }].find(d => d.id === docId) || {
                id: docId,
                type: 'Unknown',
                typeCode: 'UNK',
                date: '',
                amount: 0,
                status: 'Unknown'
              };
              return <li key={docId} className="py-4" data-id="element-4815">
                        <div className="flex items-center justify-between" data-id="element-4816">
                          <div className="flex items-center" data-id="element-4817">
                            {getDocumentTypeIcon(linkedDoc.typeCode)}
                            <div className="ml-3" data-id="element-4818">
                              <p className="text-sm font-medium text-gray-900" data-id="element-4819">
                                {linkedDoc.id}
                              </p>
                              <div className="flex items-center mt-1" data-id="element-4820">
                                <span className="text-xs text-gray-500 mr-2" data-id="element-4821">
                                  {linkedDoc.type}
                                </span>
                                <span className="text-xs text-gray-500 mr-2" data-id="element-4822">
                                  •
                                </span>
                                <span className="text-xs text-gray-500 mr-2" data-id="element-4823">
                                  {formatDate(linkedDoc.date)}
                                </span>
                                <span className="text-xs text-gray-500 mr-2" data-id="element-4824">
                                  •
                                </span>
                                <span className="text-xs text-gray-500" data-id="element-4825">
                                  {formatCurrency(linkedDoc.amount)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div data-id="element-4826">
                            <StatusBadge status={getStatusBadgeType(linkedDoc.status)} text={linkedDoc.status} data-id="element-4827" />
                          </div>
                        </div>
                      </li>;
            })}
                </ul> : <div className="text-center py-8 bg-gray-50 rounded-lg" data-id="element-4828">
                  <LinkIcon className="h-10 w-10 text-gray-400 mx-auto mb-2" data-id="element-4829" />
                  <p className="text-gray-500" data-id="element-4830">No linked documents found.</p>
                </div>}
            </div>}
        </div>
      </div>
    </div>;
}